import Dexie from 'dexie';

import { assertLocalDatabase } from '@/domain/ports/localDatabase';
import {
  createEntityStoreRegistry,
  getEntityStore
} from './entities/entityStoreRegistry';

export const WEB_DB_VERSION = 2;
export const WEB_DB_TABLES = Object.freeze([
  'tasks',
  'work_intervals',
  'sync_registry',
  'sync_cursor'
]);

const CURRENT_CURSOR_ID = 'current';

/**
 * Creates a Dexie-backed implementation of the platform-agnostic
 * LocalDatabase port.
 *
 * One IndexedDB database is created per authenticated owner. This keeps the
 * four local tables in one transactional database while preventing a later
 * login from reopening another user's records through the same adapter.
 *
 * A factory is used instead of a class so the database connection and owner
 * binding remain private to the returned adapter and methods never depend on
 * a mutable `this` context.
 *
 * @param {Object} [options] - Adapter configuration.
 * @param {string} [options.databaseName='kairos-local'] - Database prefix.
 * @returns {Object} A Dexie-backed LocalDatabase adapter.
 */
export const createWebLocalDatabase = ({
  databaseName = 'kairos-local'
} = {}) => {
  let database = null;
  let ownerId = null;
  let entityStores = null;

  const databaseNameFor = (nextOwnerId) =>
    `${databaseName}-${encodeURIComponent(nextOwnerId)}`;

  const requireOpen = () => {
    if (!database || !ownerId) {
      throw new Error('LocalDatabase must be open');
    }
  };

  /**
   * Opens the owner's database and binds this adapter to that owner.
   *
   * @param {{ownerId: string}} options - Authenticated owner context.
   * @returns {Promise<void>} Resolves when the schema is ready.
   */
  const open = ({ ownerId: nextOwnerId }) => {
    if (typeof nextOwnerId !== 'string' || nextOwnerId.length === 0) {
      return Promise.reject(new Error('ownerId is required'));
    }

    return Promise.resolve()
      .then(() => {
        if (database) {
          database.close();
        }

        ownerId = nextOwnerId;
        database = new Dexie(databaseNameFor(nextOwnerId));
        database.version(1).stores({
          tasks: 'id',
          work_intervals: 'id, task_id, started_at, ended_at',
          sync_registry:
            '[entity_type+entity_id], entity_type, entity_id, server_version',
          sync_cursor: 'id'
        });
        database.version(WEB_DB_VERSION).stores({
          tasks: 'id, updated_at',
          work_intervals: 'id, task_id, started_at, ended_at',
          sync_registry:
            '[entity_type+entity_id], entity_type, entity_id, server_version',
          sync_cursor: 'id'
        });
        entityStores = createEntityStoreRegistry(database);

        return database.open();
      })
      .then(() => undefined);
  };

  /**
   * Closes the active IndexedDB connection and removes the owner binding.
   *
   * @returns {Promise<void>} Resolves after the connection is closed.
   */
  const close = () => {
    if (database) {
      database.close();
    }
    database = null;
    ownerId = null;
    entityStores = null;
    return Promise.resolve();
  };

  /**
   * Runs work against all four tables in one Dexie read/write transaction.
   *
   * @param {(adapter: Object) => Promise<unknown>} work - Work to run.
   * @returns {Promise<unknown>} The callback result after commit.
   */
  const transaction = (work) => {
    requireOpen();
    return database.transaction(
      'rw',
      database.tasks,
      database.work_intervals,
      database.sync_registry,
      database.sync_cursor,
      () => Promise.resolve(work(adapter))
    );
  };

  const requireEntityStore = (entityType) => {
    requireOpen();
    const entityStore = getEntityStore(entityStores, entityType);

    if (!entityStore) {
      throw new TypeError(`Unsupported entity type: ${entityType}`);
    }

    return entityStore;
  };

  const getEntity = (entityType, id) => requireEntityStore(entityType).get(id);

  const listEntities = (entityType) => requireEntityStore(entityType).list();

  const listEntitiesByIds = (entityType, ids) =>
    requireEntityStore(entityType).listByIds(ids);

  const upsertEntity = (entityType, entity) =>
    requireEntityStore(entityType).upsert(entity);

  const deleteEntity = (entityType, id) =>
    requireEntityStore(entityType).delete(id);

  /**
   * @param {'task'|'work_interval'} entityType
   * @param {string} entityId
   * @returns {Promise<Object|null>}
   */
  const getSyncRecord = (entityType, entityId) => {
    requireOpen();
    return database.sync_registry
      .get([entityType, entityId])
      .then((record) => record || null);
  };

  /** @param {Object} record @returns {Promise<void>} */
  const upsertSyncRecord = (record) => {
    requireOpen();
    return database.sync_registry.put(record).then(() => undefined);
  };

  /** @returns {Promise<{last_seen_server_version: number}>} */
  const getSyncCursor = () => {
    requireOpen();
    return database.sync_cursor
      .get(CURRENT_CURSOR_ID)
      .then(
        (cursor) =>
          cursor || {
            id: CURRENT_CURSOR_ID,
            last_seen_server_version: 0
          }
      )
      .then(({ last_seen_server_version }) => ({
        last_seen_server_version
      }));
  };

  /** @param {number} serverVersion @returns {Promise<void>} */
  const setSyncCursor = (serverVersion) => {
    requireOpen();
    return database.sync_cursor
      .put({
        id: CURRENT_CURSOR_ID,
        last_seen_server_version: serverVersion
      })
      .then(() => undefined);
  };

  /**
   * Clears the current owner's domain and synchronization state atomically.
   *
   * @returns {Promise<void>} Resolves after all four stores are empty/reset.
   */
  const clearSyncState = () =>
    transaction(() =>
      Promise.all([
        database.tasks.clear(),
        database.work_intervals.clear(),
        database.sync_registry.clear(),
        database.sync_cursor.clear()
      ])
    ).then(() => undefined);

  const adapter = {
    open,
    close,
    transaction,
    getEntity,
    listEntities,
    listEntitiesByIds,
    upsertEntity,
    deleteEntity,
    getSyncRecord,
    upsertSyncRecord,
    getSyncCursor,
    setSyncCursor,
    clearSyncState
  };

  return assertLocalDatabase(adapter);
};
